import React, { useEffect, useState } from "react";
import { Box, Paper, Typography } from "@material-ui/core";
import { useTranslation } from "react-i18next";

import { getZippedGeoJson } from "../map/ZippedGeoJsonReader";
import { riskLayersEnum } from "../climateIndex/ClimateIndexConstants";
import { tableCellRender } from "../util/tableUtils";
import { DataGridCystellarTable } from "../util/DataGridCystellarTable";
import { userHasSomePermissions } from "../util/permissionsUtils";
import { PermissionIdEnum } from "../util/types/permissions/permissionIdEnum";

const RiskIndexEvaluation = (props) => {
  const { t } = useTranslation();
  const { istat } = props;

  const [tableData, setTableData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [noDataMsg, setNoDataMsg] = useState(t("levels.noData"));

  const tableColumns = [
    { headerName: t("policy.riskIndexEvaluation.riskIndexDescription"), field: "description", width: 400 },
    { headerName: t("policy.riskIndexEvaluation.riskIndex"), field: "index" },
    { headerName: t("policy.riskIndexEvaluation.nationalAverage"), field: "nationalIndex" },
  ];

  const columns = tableColumns.map((tableColumn) => ({
    valueFormatter: (params) => tableCellRender(params.value),
    filterable: false,
    width: 200,
    ...tableColumn,
  }));

  const convert5Levelsto3Levels = (severityIndex) => {
    // Convert [1..5] in geojson to [1..3] on platform
    return severityIndex && (severityIndex - 1) / 2 + 1;
  };

  useEffect(() => {
    setIsLoading(true);
    if (istat) {
      const getRiskIndex = (geojson, propertyName) => {
        if (geojson.features.length) {
          const policyComuneFeature = geojson.features.filter(
            (feature) => feature?.properties?.PRO_COM_T === istat,
          );

          if (!policyComuneFeature.length) {
            return t("policy.riskIndexEvaluation.obsoleteMunicipalityCode");
          }

          const policyComuneIndex = policyComuneFeature[0].properties[propertyName];
          return policyComuneIndex ? policyComuneIndex : null;
        }
      };

      const getZippedGeoJsonsData = async () => {
        const [cysNaturalEventsGeojson, cysNonCatEventsGeojson, aniaEventsGeojson] = await Promise.all([
          getZippedGeoJson(riskLayersEnum().cystellarNaturalEvents.geojsonUrl),
          getZippedGeoJson(riskLayersEnum().cystellarNonCatastrophe.geojsonUrl),
          // ania layers have the same geojson urls
          getZippedGeoJson(riskLayersEnum().aniaNaturalEvents.geojsonUrl),
        ]);

        let data = [
          {
            id: 1,
            description: t("climateIndex.CysNe"),
            index: getRiskIndex(
              cysNaturalEventsGeojson,
              riskLayersEnum().cystellarNaturalEvents.propertyName,
            ),
            nationalIndex: "39.08", //hardcoded for now
          },
          {
            id: 2,
            description: t("climateIndex.CysNcat"),
            index: getRiskIndex(
              cysNonCatEventsGeojson,
              riskLayersEnum().cystellarNonCatastrophe.propertyName,
            ),
            nationalIndex: "33.74", //hardcoded for now
          },
        ];
        if (userHasSomePermissions([PermissionIdEnum.Super, PermissionIdEnum.AniaNatEventsIndex])) {
          data.push({
            id: 3,
            description: t("climateIndex.AniaNe"),
            index: convert5Levelsto3Levels(
              getRiskIndex(aniaEventsGeojson, riskLayersEnum().aniaNaturalEvents.propertyName),
            ),
            nationalIndex: "-", //hardcoded for now
          });
        }
        if (userHasSomePermissions([PermissionIdEnum.Super, PermissionIdEnum.AniaNonCatIndex])) {
          data.push({
            id: 4,
            description: t("climateIndex.AniaNcat"),
            index: convert5Levelsto3Levels(
              getRiskIndex(aniaEventsGeojson, riskLayersEnum().aniaNonCatastrophe.propertyName),
            ),
            nationalIndex: "-", //hardcoded for now
          });
        }

        setIsLoading(false);
        return data;
      };

      getZippedGeoJsonsData().then((data) => setTableData(data));
    } else if (istat === null) {
      setIsLoading(false);
      setNoDataMsg(t("policy.riskIndexEvaluation.noIstatPolicyMsg"));
    }
  }, [istat, t]);

  return (
    <Box pt={1}>
      <Paper>
        <Box p={2} pt={2}>
          <Typography
            variant={"h2"}
            style={{
              fontSize: "1.3rem",
              fontWeight: 400,
            }}
          >
            {t("policy.riskIndexEvaluation.title")}
          </Typography>
        </Box>
        <div style={{ maxHeight: "40vw", maxWidth: "100%", overflow: "auto" }}>
          <DataGridCystellarTable
            columns={columns}
            rows={tableData}
            rowCount={tableData.length}
            getRowId={(rowData) => rowData.id}
            pagination={false}
            loading={isLoading}
            disableColumnFilter
            hideFooter
            disableColumnSelector
            customNoRowsMsg={noDataMsg}
          />
        </div>
      </Paper>
    </Box>
  );
};

export default RiskIndexEvaluation;
